import Api from 'entities/helpers/api';
import PropTypes from 'prop-types';
import { debounce } from '@evertrue/sdk';
import { AdvancedCombobox } from '@evertrue/et-components';
import { useEffect, useState, useCallback } from 'react';

const CustomDropdownField = ({ value, customField = {}, handleChange = () => {}, handleTouched = () => {} }) => {
  const [options, setOptions] = useState([]);
  const [total, setTotal] = useState(0);

  // Some custom fields (like the ones used in Proposal Form) already have values associated with them.
  // Others, like the ones in the Interaction Form, do not.
  // If the custom field has values, we can use them directly.
  // Otherwise, we need to fetch them from the API.
  const hasValues = customField.values && customField.values.length > 0;

  const fetchData = useCallback(
    async (like) => {
      try {
        const data = await Api.UGC.CUSTOM_FIELD_VALUES.get({
          urlArgs: { custom_field_id: customField.id },
          disableAlerts: true,
          ...(like && { params: { like } }),
        });
        const { values = [] } = data || {};
        // endpoint returns data from either ES or MYSQL so format is inconsistent
        setOptions(
          values.map((item = {}) => ({
            label: item,
            value: item,
          }))
        );
        if (!like) setTotal(values.length);
      } catch (error) {
        setOptions([]);
      }
    },
    [customField.id]
  );

  useEffect(() => {
    if (customField.values && customField.values.length > 0) {
      setOptions(
        customField.values.map((item = {}) => ({
          label: item.value,
          value: item.value,
        }))
      );
      setTotal(customField.values.length);
    } else {
      fetchData();
    }
  }, [fetchData, customField.values]);

  const debouncedSearch = debounce((val) => {
    fetchData(val);
  }, 300);

  return (
    <AdvancedCombobox
      label={customField.displayName}
      value={options?.find((v) => v.value === value)}
      options={options}
      searchable
      onSearch={total >= 250 && !hasValues ? debouncedSearch : null}
      onChange={({ value }) => handleChange(customField.id, value)}
      // onClose={handleTouched}: for some reason, having this results in Formik updating the value twice, once with the value, again with undefined. just commenting out for now.
    />
  );
};

CustomDropdownField.propTypes = {
  value: PropTypes.any,
  customField: PropTypes.shape({
    id: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    dataType: PropTypes.string.isRequired,
    required: PropTypes.bool,
    uiControlType: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleTouched: PropTypes.func,
};

export default CustomDropdownField;
